import { memo, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiAutocomplete,
  MiButton,
  MiCard,
  MiContentDrawer,
  MiDetailFields,
  MiIcon,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONTACT_TYPES,
  INPUT_TYPES,
  PREFERRED_CONTACT_METHOD,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { ClientFilter } from "@miplatform/utils";
import { mdiAccountOutline, mdiContacts, mdiMagnify, mdiPlus } from "@mdi/js";
import { MiContactCard } from "@mibuildweb/components";
import {
  Box,
  Collapse,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  contactValidationRules,
  useAddEditContactRelationViewModel,
} from "./useAddEditContactRelationViewModel";

export function AddEditContactRelationDrawer({
  selectedContact,
  defaultContactLevel = CONTACT_TYPES.COMMUNITIES,
  referenceIds,
  open,
  onRefresh,
  onClose,
}) {
  const { classes } = useStyles();
  const inputRef = useRef();
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [currentContact, setCurrentContact] = useState(
    selectedContact?.id || ""
  );
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [search, setSearch] = useState("");
  const {
    accounts,
    contacts,
    contactCategories,
    currentContactLevel,
    primaryRelationshipTypes,
    isLoading,
    getAccounts,
    getContacts,
    getContactCategoriesOptions,
    updateContactRelationship,
    addContactRelationship,
    addContact,
  } = useAddEditContactRelationViewModel({
    selectedContact,
    referenceIds,
    defaultContactLevel,
    onRefresh,
  });
  const clientFilterManager = useMemo(
    () =>
      new ClientFilter(
        contacts,
        ["displayName accountName"],
        setFilteredContacts
      ),
    [contacts]
  );
  const editInitialValues = useMemo(
    () =>
      selectedContact
        ? {
            ...selectedContact,
            contactLevel: selectedContact.level || defaultContactLevel,
            dependentType: selectedContact.label,
          }
        : { dependentType: "", contactLevel: defaultContactLevel },
    [selectedContact]
  );
  const contactEdit = useEdit(editInitialValues, [
    {
      name: "dependentType",
      validationRule: z.string().min(1, "Relationship Type is required"),
    },
  ]);
  const addContactEdit = useEdit(
    {
      accountId: "",
      dependentType: "",
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      contactLevel: defaultContactLevel,
      preferredContactMethod: PREFERRED_CONTACT_METHOD.Phone,
    },
    contactValidationRules
  );
  const contactLevel = useMemo(
    () =>
      isAddContactOpen
        ? addContactEdit.getValue("contactLevel")
        : contactEdit.getValue("contactLevel"),
    [
      isAddContactOpen,
      addContactEdit.getValue("contactLevel"),
      contactEdit.getValue("contactLevel"),
    ]
  );

  const contactFields = [
    {
      label: "Account",
      name: "accountId",
      fieldType: INPUT_TYPES.SELECT,
      required: true,
      options: accounts,
      width: "50%",
    },
    {
      label: "Title",
      name: "title",
      width: "50%",
    },
    {
      label: "First Name",
      name: "firstName",
      required: true,
      width: "50%",
    },
    {
      label: "Last Name",
      name: "lastName",
      required: true,
      width: "50%",
    },
    {
      label: "Email",
      name: "email",
      required: true,
      width: "50%",
    },
    {
      label: "Phone Number",
      name: "phone",
      required: true,
      width: "50%",
    },
    {
      label: "Relationship Type",
      name: "dependentType",
      fieldType: "select",
      options: contactCategories,
      required: true,
      width: "34%",
      handleChange: (e) =>
        addContactEdit.update({
          isPrimary: false,
          dependentType: e.target.value,
        }),
    },
    {
      label: "Primary Contact",
      name: "isPrimary",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: !primaryRelationshipTypes.has(
        addContactEdit.getValue("dependentType")
      ),
    },
    {
      label: "VIP",
      name: "isVip",
      fieldType: INPUT_TYPES.CHECKBOX,
    },
  ];

  const handleAutoCompleteOpen = () => setIsAutocompleteOpen(true);

  const handleAutoCompleteClose = () => {
    setIsAutocompleteOpen(false);
    inputRef.current?.blur();

    if (isAddContactOpen && currentContact) {
      setCurrentContact("");
    }
  };

  const handleOpenAddContact = (e) => {
    e.preventDefault();
    handleAutoCompleteClose();
    clientFilterManager.removeAllFilters();
    contactEdit.update({ id: "" });
    setIsAddContactOpen(true);
  };

  const handleCloseAddContact = () => setIsAddContactOpen(false);

  const editContactRelationshipModalFields = [
    {
      label: "Relationship Type",
      name: "dependentType",
      fieldType: "select",
      options: contactCategories,
      required: true,
      handleChange: (e) =>
        contactEdit.update({ isPrimary: false, dependentType: e.target.value }),
    },
    {
      label: "Primary Contact",
      name: "isPrimary",
      fieldType: "switch",
      readOnly: !primaryRelationshipTypes.has(
        contactEdit.getValue("dependentType")
      ),
    },
  ];

  const handleSearch = (_e, value, reason) => {
    if (reason === "reset") {
      return;
    }

    setSearch(value);
    clientFilterManager.handleSearch(value);

    if (currentContact) {
      setCurrentContact("");
    }
  };

  const handleContactSelect = (value) => {
    setCurrentContact(value.id);
    contactEdit.update({
      ...value,
      dependentType: "",
      contactLevel: defaultContactLevel,
      isPrimary: null,
    });
    handleCloseAddContact();
    addContactEdit.reset();
  };

  const handleDrawerClose = () => {
    contactEdit.reset();
    addContactEdit.reset();
    setSearch("");
    setCurrentContact("");
    setIsAddContactOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    const isSuccessful = await (() => {
      if (selectedContact) {
        const { id, accountId, dependentType, isPrimary } =
          contactEdit.getValue();

        return updateContactRelationship({
          contactId: id,
          accountId,
          label: dependentType,
          isPrimary,
        });
      }
      if (isAddContactOpen) {
        const { dependentType, ...edits } = addContactEdit.getValue();

        return addContact({
          ...edits,
          label: dependentType,
        });
      }
      const { id, accountId, dependentType, isPrimary } =
        contactEdit.getValue();

      return addContactRelationship({
        contactId: id,
        accountId,
        label: dependentType,
        isPrimary,
      });
    })();
    if (isSuccessful) {
      handleDrawerClose();
    }
  };

  useEffect(() => {
    getAccounts();
    getContactCategoriesOptions(currentContactLevel);
    getContacts();
  }, []);

  useEffect(() => {
    if (currentContactLevel !== contactLevel) {
      getContactCategoriesOptions(contactLevel);
    }
  }, [currentContactLevel, contactLevel]);

  return (
    <MiContentDrawer
      open={open}
      variant="temporary"
      isLoading={isLoading}
      leftIcon={mdiAccountOutline}
      title={selectedContact ? "Edit Contact" : "Add Contact"}
      onClose={handleDrawerClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleDrawerClose,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          disabled:
            !(isAddContactOpen
              ? addContactEdit.isValid()
              : contactEdit.isValid()) || isLoading,
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSubmit,
        },
      ]}
    >
      <Box flex={1}>
        <MiCard
          title={selectedContact ? "Contact Details" : "Search Contacts"}
          leftIcon={mdiContacts}
        >
          {!selectedContact && (
            <Box className={classes.innerContainer}>
              <MiAutocomplete
                size="small"
                fullWidth
                disableClearable
                forcePopupIcon={false}
                open={isAutocompleteOpen}
                filterOptions={(options) => options}
                options={filteredContacts}
                getOptionLabel={(value) => {
                  const contact = contacts.find(
                    ({ id }) => id === value || id === value?.id
                  );

                  return contact
                    ? `${contact.displayName} ${contact.accountName}`
                    : "";
                }}
                value={currentContact}
                inputValue={search}
                getOptionKey={({ id }) => id}
                onInputChange={handleSearch}
                isOptionEqualToValue={(value, option) => option === value.id}
                onChange={(_e, value) => handleContactSelect(value)}
                onOpen={handleAutoCompleteOpen}
                onClose={handleAutoCompleteClose}
                renderOption={(
                  { ...props },
                  { id, displayName, accountName }
                ) => (
                  <MenuItem key={id} {...props}>
                    <Typography lineHeight={2}>
                      {`${displayName} | ${accountName}`}
                    </Typography>
                  </MenuItem>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={inputRef}
                    placeholder="Search Contacts"
                    InputProps={{
                      ...params.InputProps,

                      "data-testid": "mi-autocomplete-input-search",
                      endAdornment: (
                        <InputAdornment>
                          <MiIcon path={mdiMagnify} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                slotProps={{
                  paper: {
                    onMouseDown: handleOpenAddContact,
                  },
                }}
                PaperComponent={AutocompletePaper}
              />
            </Box>
          )}
          <Collapse in={Boolean(contactEdit.getValue("id"))} unmountOnExit>
            <Box
              display="flex"
              flexDirection="column"
              paddingTop={2}
              backgroundColor="common.white"
            >
              <MiContactCard
                readOnly
                elevation={0}
                contact={contactEdit.getValue()}
              />
              <Box paddingX={2} display="flex" flexDirection="column" gap={2}>
                <Divider />
                <MiDetailFields
                  detailFields={editContactRelationshipModalFields}
                  edit={contactEdit}
                />
              </Box>
            </Box>
          </Collapse>
        </MiCard>
        <Collapse in={isAddContactOpen} unmountOnExit>
          <MiCard title="Add Contact" leftIcon={mdiAccountOutline}>
            <Box backgroundColor="common.white">
              <MiDetailFields
                detailFields={contactFields}
                edit={addContactEdit}
              />
            </Box>
          </MiCard>
        </Collapse>
      </Box>
    </MiContentDrawer>
  );
}

const AutocompletePaper = memo(({ children, onMouseDown, ...props }) => (
  <Paper {...props}>
    {children}
    <MiButton
      fullWidth
      title="Create New Contact"
      startIcon={{ path: mdiPlus }}
      sx={{
        borderRadius: 0,
        justifyContent: "flex-start",
        color: "blue.main",
        pl: 4,
      }}
      onMouseDown={onMouseDown}
    />
  </Paper>
));

const useStyles = makeStyles()((theme) => ({
  innerContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
  },
}));
